import { useRef, FocusEvent, KeyboardEvent } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { SxProps, TextFieldProps } from '@mui/material'
import { DatePicker, TimePicker, DateTimePicker } from '@mui/x-date-pickers'

const typeMap = {
  date: DatePicker,
  datetime: DateTimePicker,
  time: TimePicker,
}

export type DateTimeValueInputProps<PropsType> = {
  value: Dayjs | null
  handleSubmit?: (value: string) => void
  onClose?: () => void
  type?: 'date' | 'datetime' | 'time'
  onKeyDown?: (e: React.KeyboardEvent, callback: () => void) => void
  sx?: SxProps
  inputRef?: React.Ref<HTMLInputElement>
  required?: boolean
  fullWidth?: boolean
  autoFocus?: TextFieldProps['autoFocus']
  variant?: TextFieldProps['variant']
  InputLabelProps?: TextFieldProps['InputLabelProps']
} & PropsType

export default function DateTimeValueInput<PropsType>({
  value,
  handleSubmit,
  onClose,
  type = 'date',
  onKeyDown,
  sx,
  fullWidth,
  InputLabelProps,
  autoFocus = false,
  variant = 'filled',
  ...props
}: DateTimeValueInputProps<PropsType>) {
  const Component = typeMap[type]
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const popperRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const isPickerOpenRef = useRef(false)

  function prepForSubmit(value: Dayjs | null) {
    if (!value) {
      handleSubmit?.('')
    } else if (value.isValid()) {
      handleSubmit?.(value.toISOString())
    }
  }

  function getInputValueAsDayjs() {
    const rawValue = inputRef.current?.value
    if (!rawValue) return null

    const parsedValue = dayjs(rawValue)
    return parsedValue.isValid() ? parsedValue : null
  }

  // This blur handler has to go on the containing element so it will trigger
  // when they tab off the date picker button, not just the input field.
  function handleContainerBlur(
    e: FocusEvent<HTMLDivElement | HTMLButtonElement>,
  ) {
    // This set timeout pushes this logic to the next event cycle,
    // giving the date picker overlay the chance to open (if it is supposed to)
    setTimeout(() => {
      const activeElement = document.activeElement

      if (
        // Never submit/close from blur while the picker popper is open.
        !isPickerOpenRef.current &&
        // Check if focus is outside of the date picker input/button container.
        !containerRef.current?.contains(activeElement) &&
        // Check if focus is outside of the date picker overlay.
        !popperRef.current?.contains(activeElement) &&
        // Check that we didn't click the date picker button.
        e.target !== buttonRef.current
      ) {
        prepForSubmit(getInputValueAsDayjs())
        onClose?.()
      }
    }, 0)
  }

  function handleKeyDown(e: KeyboardEvent) {
    onKeyDown?.(e, () => prepForSubmit(getInputValueAsDayjs()))
  }

  return (
    <div ref={containerRef} onBlur={handleContainerBlur}>
      <Component
        // Coerce undefined to null so the MUI picker stays controlled from the
        // first render (an undefined value reads as uncontrolled, then flips to
        // controlled once a date is set).
        value={value ?? null}
        inputRef={inputRef}
        slotProps={{
          field: {
            clearable: true,
          },
          openPickerButton: {
            ref: buttonRef,
          },
          textField: {
            autoFocus,
            size: 'small',
            onKeyDown: handleKeyDown,
            variant,
            sx,
            fullWidth,
            InputLabelProps,
          },
        }}
        onOpen={() => {
          isPickerOpenRef.current = true
        }}
        onClose={() => {
          isPickerOpenRef.current = false
          onClose?.()
        }}
        onAccept={prepForSubmit}
        onChange={() => {}} // Required by the component, but not needed by us
        PopperProps={{ ref: popperRef }}
        {...props}
      />
    </div>
  )
}
